import { createHooks } from "hookable";
import type {
  BioSitePage,
  BioSiteSocialItem,
  BioSiteStateTransfer,
} from "./remote";
import type { HostAction } from "./types";

interface EventNames {
  exit: { (): Promise<void> | void };
  // save: { (done: { (): void }): Promise<void> | void };
  loaded: { (site: BioSiteStateTransfer): Promise<void> | void };
  updated: { (site: BioSiteStateTransfer): Promise<void> | void };
  saved: { (): Promise<void> | void };
  "remote-action": { (action: HostAction): Promise<void> | void };
  "remote-data": { (id: string, payload: any): Promise<void> | void };
  "content:add-socials": { (): void };
  "content:edit-social-item": { (item: BioSiteSocialItem): void };
  "content:edit-image": { (): void };
  "controls:page-changed": { (): void };
  "controls:configure-pages": { (): void };
  "controls:edit-page": { (page: BioSitePage): void };
  "controls:delete-page": { (page: BioSitePage): void };
  "controls:add-page": { (): void };
}

const events = createHooks<EventNames>();

export const on = <E extends keyof EventNames>(event: E, fn: EventNames[E]) => {
  const unsub = events.hook(event as any, fn as any);

  // auto unsub
  setTimeout(() => {
    const instance = getCurrentInstance();
    if (instance) {
      onUnmounted(unsub);
    }
  }, 1000);
};

export const off = <E extends keyof EventNames>(
  event: E,
  fn: EventNames[E]
) => {
  events.removeHook(event, fn as any);
};

export const once = <E extends keyof EventNames>(
  event: E,
  fn: EventNames[E]
) => {
  events.hookOnce(event, fn as any);
};

export const emit = <E extends keyof EventNames>(
  event: E,
  ...a: Parameters<EventNames[E]>
) => {
  events.callHook(event, ...a);
};

const make = <E extends keyof EventNames>(event: E) => {
  return (fn: EventNames[E]) => events.hook(event, fn as any);
};

export const onUpdated = make("updated");
export const onSaved = make("saved");
export const onExit = make("exit");
